import { ACTION_CLICK_MARKETING_FEATURE_CARD } from 'utils/analytics/eventTypeConstantsAmplitude';
import { LazyBackgroundImage } from 'ui';
import { Link, Typography } from 'omakase';
import { consumer } from '@tock/coretypes';
import { getAutoFormatUrl } from '@tock/images';
import { getTockContent, getTockMedium } from 'utils/widgetUtils';
import { makeStyles } from '@material-ui/core/styles';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useLocation } from 'react-router';
import OrphanProtected from 'components/OrphanProtected';
import WidgetLayoutContainer from 'consumer-page-widgets/WidgetUtils/WidgetLayoutContainer';
import clsx from 'clsx';

/**
 * IMPORTANT!!
 * If you are updating these styles, you should be sure to update the styles in clowncar's "MarketingFeaturesWidgetPreview.tsx"
 * That file is supposed to replicated these styles so that marketing can see a preview.
 */
const useStyles = makeStyles((theme) => {
  const timeAndEase = `${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`;
  return {
    root: {
      marginBottom: theme.spacing(6),
    },
    containerDesktop: {
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'block',
      },
    },
    featuresDesktop: {
      display: '-ms-grid',
      '&': { display: 'grid' },
      gap: `${theme.spacing(3)}px`,
      gridAutoColumns: '1fr',
      gridAutoRows: '1fr',
      '-ms-grid-columns': '1fr 1fr 1fr 1fr',
      '-ms-grid-rows': '1fr 1fr',
      width: '100%',
    },
    featureDesktop: {
      backgroundColor: theme.colors.bgDarker,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      '& > a': {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'flex-end',
        overflowY: 'hidden',
        '& .MarketingFeature-backdrop': {
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          display: 'block',
          flex: 1,
          opacity: 0,
          transition: `${theme.transitions.duration.enteringScreen}ms opacity ${theme.transitions.easing.easeInOut}`,
          width: '100%',
          '&--inView': {
            opacity: 1,
          },
        },
        '& .MarketingFeature-meta': {
          background: theme.colors.bgDarker,
          boxShadow: `0 ${theme.spacing(1)}px 0 0 ${theme.colors.borderDark}`,
          padding: theme.spacing(3),
          transform: 'translateY(0) translateZ(0)',
          transition: `background ${timeAndEase}, box-shadow ${timeAndEase}, transform ${timeAndEase}`,
        },
        '&:hover, &:focus': {
          '& .MarketingFeature-backdrop': {},
        },
        '&:hover': {
          textDecoration: 'none',
          '& .MarketingFeature-meta': {
            background: theme.colors.bgContrast,
            boxShadow: `0 ${theme.spacing(1)}px 0 0 ${theme.colors.borderDark}`,
            transform: `translateY(${theme.spacing(-1)}px) translateZ(0)`,
          },
          '&.Mui-focusVisible .MarketingFeature-meta': {
            boxShadow: `0 ${theme.spacing(1)}px 0 0 ${theme.colors.borderPrimaryBase}`,
          },
        },
        '&:focus': {
          boxShadow: 'none',
        },
        '&.Mui-focusVisible': {
          '&, &:focus': {
            boxShadow: `0 0 0 2px ${theme.colors.borderPrimaryBase}`,
            '& .MarketingFeature-meta': {
              background: theme.colors.bgPrimaryBase,
            },
          },
        },
      },
      height: 280,
      [theme.breakpoints.down('sm')]: {
        '&:nth-child(odd)': {
          gridColumn: '1 / span 2',
          '&': {
            '-ms-grid-column': '1',
            '-ms-grid-column-span': '2',
          },
        },
        '&:nth-child(even)': {
          gridColumn: '3 / span 2',
          '&': {
            '-ms-grid-column': '3',
            '-ms-grid-column-span': '2',
          },
        },
        '&:nth-child(1), &:nth-child(2)': {
          '-ms-grid-row': '1',
        },
        '&:nth-child(3), &:nth-child(4)': {
          '-ms-grid-row': '2',
        },
        '&.featureDesktopOfThree:nth-child(3)': {
          gridColumn: '1 / span 4',
          '&': {
            '-ms-grid-column': '1',
            '-ms-grid-column-span': '4',
          },
        },
      },
      [theme.breakpoints.up('md')]: {
        height: 'auto',
        '&:nth-child(1)': {
          gridColumn: '1 / span 2',
          gridRow: '1 / span 2',
          '&': {
            '-ms-grid-column': '1',
            '-ms-grid-column-span': '2',
            '-ms-grid-row': '1',
            '-ms-grid-row-span': '2',
          },
          height: 500,
        },
        '&:nth-child(2)': {
          '&.featureDesktopOfThree': {
            gridColumn: '3 / span 2',
            gridRow: '1 / 2',
            '&': {
              '-ms-grid-column': '3',
              '-ms-grid-column-span': '2',
              '-ms-grid-row': '1',
            },
          },
          '&.featureDesktopOfFour, &.featureDesktopOfFive': {
            gridColumn: '3 / 3',
            gridRow: '1 / 1',
            '&': {
              '-ms-grid-column': '3',
              '-ms-grid-column-span': '1',
              '-ms-grid-row': '1',
            },
          },
        },
        '&:nth-child(3)': {
          '&.featureDesktopOfThree': {
            gridColumn: '3 / span 2',
            gridRow: '2 / 2',
            '&': {
              '-ms-grid-column': '3',
              '-ms-grid-column-span': '2',
              '-ms-grid-row': '2',
            },
          },
          '&.featureDesktopOfFour, &.featureDesktopOfFive': {
            gridColumn: '4 / 4',
            gridRow: '1 / 1',
            '&': {
              '-ms-grid-column': '4',
              '-ms-grid-column-span': '1',
              '-ms-grid-row': '1',
            },
          },
        },
        '&:nth-child(4)': {
          '&.featureDesktopOfFour': {
            gridColumn: '3 / span 2',
            gridRow: '2 / 2',
            '&': {
              '-ms-grid-column': '3',
              '-ms-grid-column-span': '2',
              '-ms-grid-row': '2',
            },
          },
          '&.featureDesktopOfFive': {
            gridColumn: '3 / 4',
            gridRow: '2 / 2',
            '&': {
              '-ms-grid-column': '3',
              '-ms-grid-column-span': '1',
              '-ms-grid-row': '2',
            },
          },
        },
        '&:nth-child(5)': {
          gridColumn: '4 / 4',
          gridRow: '2 / 2',
          '&': {
            '-ms-grid-column': '4',
            '-ms-grid-column-span': '1',
            '-ms-grid-row': '2',
          },
        },
      },
      [theme.breakpoints.up('lg')]: {
        '&:nth-child(1)': {
          height: 600,
        },
        '& .MarketingFeature-meta': {
          paddingRight: theme.spacing(4),
        },
      },
    },
    featuresMobile: {
      background: theme.colors.bgDarker,
      display: 'block',
      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },
    featureMobile: {
      marginRight: theme.spacing(3),
      scrollSnapAlign: 'start',
      '& > a': {
        display: 'block',
        padding: 2,
        position: 'relative',
        width: '26rem',
        '&:hover': {
          textDecoration: 'none',
        },
      },
      '& .MarketingFeature-backdrop-wrapper': {
        backgroundColor: theme.colors.bgLight,
        height: '100%',
        left: 0,
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        width: '100%',
        zIndex: 1,
        '& .MarketingFeature-backdrop': {
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          opacity: 0,
          transition: `${theme.transitions.duration.enteringScreen}ms opacity ${theme.transitions.easing.easeInOut}`,
          display: 'block',
          filter: 'blur(12px) brightness(1.1)',
          height: '100%',
          transform: 'scale(1.1)',
          width: '100%',
          '&--inView': {
            opacity: 1,
          },
        },
      },
      '& .MarketingFeature-content-wrapper': {
        background: theme.colors.bgDarker,
        padding: theme.spacing(1),
        position: 'relative',
        zIndex: 2,
        '& .MarketingFeature-image': {
          backgroundPosition: 'center',
          opacity: 0,
          transition: `${theme.transitions.duration.enteringScreen}ms opacity ${theme.transitions.easing.easeInOut}`,
          backgroundSize: 'cover',
          display: 'block',
          width: '100%',
          '&::after': {
            content: '""',
            display: 'block',
            paddingBottom: '100%',
          },
          '&--inView': {
            opacity: 1,
          },
        },
        '& .MarketingFeature-meta': {
          padding: `${theme.spacing(2.5)}px ${theme.spacing(1)}px ${theme.spacing(
            2.5
          )}px ${theme.spacing(1.5)}px`,
        },
      },
    },
    featuresMobileScrollContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-around',
      overflow: 'hidden',
      marginLeft: theme.spacing(-2.5),
      marginRight: theme.spacing(-2.5),
      '& .scroll-area': {
        display: 'flex',
        flexWrap: 'nowrap',
        overflowX: 'auto',
        scrollPadding: `${theme.spacing(3)}px`, // something in material-ui wasn't adding px to this
        paddingTop: theme.spacing(9),
        paddingBottom: theme.spacing(9),
        transform: 'translateZ(0)',
        '& > *:first-child': {
          paddingLeft: 'calc((100vw - 26rem) / 2)',
        },
        '& > *:last-child': {
          paddingRight: 'calc((100vw - 26rem) / 2)',
        },
      },
    },
    headingText: {
      fontWeight: theme.typography.fontWeightBold,
    },
    labelText: {
      fontSize: 12,
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: 16 / 12,
      [theme.breakpoints.up('lg')]: {
        fontSize: 14,
        lineHeight: 16 / 14,
      },
    },
  };
});

interface Props {
  name?: string;
  tile: consumer.IMarketingFeatureTile[];
  pageIndex?: number;
}

const MarketingFeaturesWidget: React.FunctionComponent<Props> = ({ name, tile, pageIndex }) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const classes = useStyles();
  const location = useLocation();
  const medium = getTockMedium(location.pathname);
  const widgetAnalyticsCategory = `MFW-ExploreTock-${medium}`;

  const tileCount = tile?.length;

  const onClick = ({ tileName, tileListIndex }: { tileName?: string; tileListIndex: number }) => {
    amplitudeSendEvent(ACTION_CLICK_MARKETING_FEATURE_CARD, {
      title: tileName,
      listIndex: tileListIndex,
      pageWidgetListIndex: pageIndex,
      widgetName: name || undefined,
    });
    if (!window.analytics) {
      return;
    }
    window.analytics.sendEvent(widgetAnalyticsCategory, 'Item Click', tileName);
  };

  const utmFromFeature = (href: string, title: string) =>
    `${href}${
      href.match(/\?/) ? '&' : '?'
    }tock_source=tock&tock_medium=${medium}&tock_content=${getTockContent(
      'marketingfeatures',
      title
    )}`;

  return (
    <div className={classes.root}>
      <>
        <WidgetLayoutContainer className={classes.containerDesktop}>
          <div className={classes.featuresDesktop}>
            {tile.map(({ imageUrl, title, text, link }, idx) => (
              <div
                className={clsx(classes.featureDesktop, {
                  featureDesktopOfFive: tileCount === 5,
                  featureDesktopOfFour: tileCount === 4,
                  featureDesktopOfThree: tileCount === 3,
                  featureDesktopOfTwo: tileCount === 2,
                })}
                key={link}
              >
                <Link
                  underline="none"
                  href={utmFromFeature(link ?? '', title ?? '')}
                  onClick={() => onClick({ tileListIndex: idx, tileName: title || undefined })}
                >
                  <LazyBackgroundImage
                    url={getAutoFormatUrl(imageUrl ?? undefined) ?? ''}
                    className="MarketingFeature-backdrop"
                    inViewClassName="MarketingFeature-backdrop--inView"
                  />
                  <div className="MarketingFeature-meta">
                    <Typography
                      color="fgContrast"
                      component="h3"
                      variant="heading03"
                      className={classes.headingText}
                    >
                      {title}
                    </Typography>
                    <Typography color="fgContrast" variant="body02" className={classes.labelText}>
                      <OrphanProtected string={text ?? ''} />
                    </Typography>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </WidgetLayoutContainer>
        <WidgetLayoutContainer className={classes.featuresMobile}>
          <div className={classes.featuresMobileScrollContainer}>
            <div className="scroll-area">
              {tile.map(({ imageUrl, title, text, link }, idx) => (
                <div key={link} className={classes.featureMobile}>
                  <Link
                    href={link ?? ''}
                    onClick={() => onClick({ tileListIndex: idx, tileName: title || undefined })}
                  >
                    <div className="MarketingFeature-backdrop-wrapper">
                      <LazyBackgroundImage
                        url={getAutoFormatUrl(imageUrl ?? undefined) ?? ''}
                        className="MarketingFeature-backdrop"
                        inViewClassName="MarketingFeature-backdrop--inView"
                      />
                    </div>
                    <div className="MarketingFeature-content-wrapper">
                      <div className="MarketingFeature-meta">
                        <Typography color="fgContrast" mb={1} component="h2" variant="decorative">
                          {title}
                        </Typography>
                        <Typography
                          color="fgContrast"
                          variant="body02"
                          className={classes.labelText}
                        >
                          <OrphanProtected string={text ?? ''} />
                        </Typography>
                      </div>
                      <LazyBackgroundImage
                        url={getAutoFormatUrl(imageUrl ?? undefined) ?? ''}
                        className="MarketingFeature-image"
                        inViewClassName="MarketingFeature-image--inView"
                      />
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </WidgetLayoutContainer>
      </>
    </div>
  );
};

export default MarketingFeaturesWidget;
